import dvaModelExtend from 'dva-model-extend';
import _ from 'lodash';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import { createCrudModel } from '@/utils/core/dva';
import { CoreEnums } from '@/utils/domain/constants';
import {
  getBulkImportErrorData,
  getFiltersGlobalFilters,
  toPaginate,
} from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { queryAddUserGroupUser, queryUserGroupUsers } from './service';

const api = bindServices('userGroups');
const { BulkActionType } = CoreEnums;

const namespace = 'userGroups';
const fetchRoute = 'userGroup';
const fetchParams = 'userGroup';

const _model = createCrudModel({
  namespace,
  fetchRoute,
  fetchParams,
  entityName: 'userGroup',
  api,
  features: { import: true, bulkActions: false },
  state: {
    newUserOnGroup: false,
    groupUsersItem: null,
    formConfigGroupUsers: null,
    listUserGroupUser: [],
  },
  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = { ...CoreQueries[fetchParams].paged, pageSize, ...payload };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        const items = res?.items || res?.data || [];
        const page = payload.page ?? 1;
        const limit = payload.limit ?? pageSize;
        const totalCount = res?.totalCount ?? res?.count ?? res?.total ?? items.length;
        yield put({ type: 'queryData', payload: { ...res, limit, page, totalCount, items } });
      } catch (e) {}
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {}
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id } = payload;
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
        yield put({
          type: 'queryShowForm',
          payload: { state: false, item: null, detail: false, id: null },
        });
        yield call(api.remove, { route: fetchRoute, id });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: payload.params || {} });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeleteUserGroups({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;
        for (const id of ids) {
          yield call(api.remove, { route: fetchRoute, id });
        }

        const pageSize = yield select((state) => state.global.pageSize);
        const state = yield select((s) => s.userGroups);
        const globalState = yield select((s) => s.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(state.orderBy && { orderBy: state.orderBy }),
          ...(filters && { filter: filters }),
          select: state.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateUserGroups({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkUserGroupActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkDeactivateUserGroups({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkUserGroupActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *addNewUserOnGroup({ payload }, { put }) {
      yield put({ type: 'addUserOnGroup', payload });
    },

    *fetchUserOnGroup({ payload }, { call, put }) {
      try {
        const response = yield call(queryUserGroupUsers, payload);
        yield put({ type: 'listUserOnGroup', response });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: false, visibleForm: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *hideNewUserOnGroup({ payload = 'hideUserOnGroup' }, { put }) {
      yield put({ type: payload });
    },

    *onCloseUserOnGroup(_, { put }) {
      yield put({ type: 'closeUserOnGroup' });
    },

    *addUserGroupUser({ payload }, { put }) {
      yield put({ type: 'addUserGroupList', payload });
    },

    *saveUserGroupUsers({ payload }, { call, put }) {
      try {
        yield call(queryAddUserGroupUser, payload);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'addUserOnGroup',
          payload: { state: false, item: null, form: null },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: false, visibleForm: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *deleteItemUserGroup({ id }, { put }) {
      try {
        yield put({ type: 'deleteUserGroupUser', payload: id });
        yield put({
          type: 'global/modalConfirmDrawer',
          payload: { state: false, id: null },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchImport({ payload }, { call, put, select }) {
      try {
        const { file, isDeleteData } = payload;
        const pageSize = yield select((state) => state.global.pageSize);
        const stateModel = yield select((state) => state.userGroups);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateModel.orderBy && { orderBy: stateModel.orderBy }),
          ...(filters && { filter: filters }),
        };

        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import`,
          method: 'post',
          data: { fileContent: file, deleteExistingData: isDeleteData },
        });

        const errorData = getBulkImportErrorData(res, getIntl);
        const hasImportedRows = res.importedRowsCount && res.importedRowsCount > 0;

        if (errorData) {
          yield put({ type: 'setBulkActionErrors', payload: { errorData } });
          yield put({ type: 'toggleBulkActionErrorsDrawer', payload: { open: true } });
        }

        yield put({ type: 'queryImport', payload: { ...res } });
        if (hasImportedRows) yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },
  },
  reducers: {
    queryData(state, { payload }) {
      const items = payload.items ?? payload.data ?? state.list;
      return {
        ...state,
        list: items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    userGroupActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },

    bulkUserGroupActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    addUserOnGroup(state, { payload }) {
      return {
        ...state,
        newUserOnGroup: payload.state,
        groupUsersItem: payload.item,
        formConfigGroupUsers: payload.form,
      };
    },

    hideUserOnGroup(state) {
      return {
        ...state,
        groupUsersItem: null,
        listUserGroupUser: [],
        formConfigGroupUsers: null,
      };
    },

    closeUserOnGroup(state) {
      return { ...state, newUserOnGroup: false };
    },

    listUserOnGroup(state, { response }) {
      return {
        ...state,
        listUserGroupUser: (response || []).map((item) => ({
          id: item.id,
          fullName: item.fullName,
          user: item.fullName,
        })),
      };
    },

    addUserGroupList(state, { payload }) {
      return {
        ...state,
        listUserGroupUser: [...state.listUserGroupUser, payload],
      };
    },

    deleteUserGroupUser(state, { payload }) {
      return {
        ...state,
        listUserGroupUser: state.listUserGroupUser.filter((item) => item.id !== payload),
      };
    },
  },
});

export default dvaModelExtend(_model, { namespace });
